import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import {
  BookOpen, 
  FileText, 
  ClipboardCheck,
  Target,
  Layers,
  CheckCircle2,
  Printer,
  GraduationCap,
  MessageSquare,
  PenTool,
  Clock,
  Sparkles,
  Calendar,
  Download,
  FolderOpen
} from 'lucide-react';

import { eslCurriculum, eslYearlyPlanner, eslResources } from '@/data/eslCurriculum';
import { eslWeeklyLessons } from '@/data/eslWeeklyLessons';
import AILessonPlanGenerator from '@/components/curriculum/AILessonPlanGenerator';
import TermExamGenerator from '@/components/curriculum/TermExamGenerator';
import QuickPrintAll from '@/components/curriculum/QuickPrintAll';
import { FileQuestion, Files } from 'lucide-react';
import LessonPlanGenerator from '@/components/curriculum/LessonPlanGenerator';
import SubjectSchemeOfWork from '@/components/curriculum/SubjectSchemeOfWork';
import { getCurrentUser } from '@/data/users';

export default function ESLCurriculumPage() {
  const [selectedYear, setSelectedYear] = useState("Year 8");
  const [activeTab, setActiveTab] = useState("curriculum");
  const [selectedTerm, setSelectedTerm] = useState("term1");
  const [teacherYears, setTeacherYears] = useState(['8', '9', '10', '11', '12', '13']);
  
  const currentUser = getCurrentUser();
  const restrictedTo = currentUser?.restrictedTo || currentUser?.restricted_to;
  const department = currentUser?.department || '';
  
  useEffect(() => {
    // Check if user has restrictedTo set (e.g., "9" for Year 9 only)
    if (restrictedTo) {
      let allowedYears = [];
      const restrictedStr = String(restrictedTo);
      if (restrictedStr.includes(',')) {
        // Comma-separated: "11,13"
        allowedYears = restrictedStr.split(',').map(s => s.trim());
      } else if (restrictedStr.includes('-')) {
        const [start, end] = restrictedStr.split('-').map(Number);
        for (let i = start; i <= end; i++) {
          allowedYears.push(String(i));
        }
      } else {
        allowedYears = [restrictedStr.trim()];
      }
      setTeacherYears(allowedYears);
      setSelectedYear(`Year ${allowedYears[0]}`);
      return;
    }
    
    // Check department field for year information (e.g., "Year 9 English" or "History & Year 9 English")
    if (department) {
      const englishMatch = department.match(/year\s*(\d+)\s*english/i);
      const eslMatch = department.match(/year\s*(\d+)\s*esl/i);
      
      if (englishMatch || eslMatch) {
        const year = englishMatch ? englishMatch[1] : eslMatch[1];
        setTeacherYears([year]);
        setSelectedYear(`Year ${year}`);
        return;
      }
    }
    
    // Otherwise check user's classes
    if (currentUser?.classes) {
      const years = currentUser.classes
        .filter(c => c.toLowerCase().includes('english') || c.toLowerCase().includes('esl'))
        .map(c => {
          const match = c.match(/year\s*(\d+)/i);
          return match ? match[1] : null;
        })
        .filter(Boolean);
      if (years.length > 0) {
        setTeacherYears([...new Set(years)]);
        setSelectedYear(`Year ${years[0]}`);
      }
    }
  }, [restrictedTo, department]);

  const yearKey = selectedYear.toLowerCase().replace(' ', '');
  const curriculum = eslCurriculum[yearKey] || eslCurriculum.year8;

  const getIconComponent = (iconName) => {
    const icons = {
      BookOpen: BookOpen,
      FileText: FileText,
      PenTool: PenTool,
      MessageSquare: MessageSquare
    };
    return icons[iconName] || BookOpen;
  };

  const getSkillLevelBadge = (level) => {
    const levels = {
      1: { label: 'Uni-Structural', color: 'bg-blue-100 text-blue-800' },
      2: { label: 'Multi-Structural', color: 'bg-green-100 text-green-800' },
      3: { label: 'Relational', color: 'bg-purple-100 text-purple-800' },
      4: { label: 'Extended Abstract', color: 'bg-orange-100 text-orange-800' }
    };
    const levelInfo = levels[level] || levels[1];
    return <Badge className={levelInfo.color}>{levelInfo.label}</Badge>;
  };

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="esl-curriculum-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
            <BookOpen className="h-8 w-8 text-purple-600" />
            English as Second Language Curriculum
          </h1>
          <p className="text-gray-500 mt-1">Vanuatu National Syllabus - {parseInt(selectedYear.replace('Year ', '')) <= 10 ? 'Junior' : 'Senior'} Secondary</p>
        </div>
        <div className="flex items-center gap-4">
          <Select value={selectedYear} onValueChange={setSelectedYear}>
            <SelectTrigger className="w-[140px]" data-testid="year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {teacherYears.map(year => (
                <SelectItem key={year} value={`Year ${year}`}>Year {year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" className="hidden md:flex items-center gap-2">
            <Printer className="h-4 w-4" />
            Print
          </Button>
        </div>
      </div>

      {/* Year Overview Card */}
      <Card className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold text-purple-900">{curriculum.title}</h2>
              <p className="text-purple-700 mt-1">{curriculum.description}</p>
            </div>
            <div className="flex gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">{curriculum.hours}</div>
                <div className="text-sm text-purple-500">Hours/Year</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{curriculum.strands?.length || 4}</div>
                <div className="text-sm text-blue-500">Strands</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 lg:grid-cols-8 lg:w-auto lg:inline-grid">
          <TabsTrigger value="curriculum" className="flex items-center gap-2">
            <Layers className="h-4 w-4" />
            <span className="hidden sm:inline">Curriculum</span>
          </TabsTrigger>
          <TabsTrigger value="yearly-planner" className="flex items-center gap-2 bg-green-100">
            <Calendar className="h-4 w-4" />
            <span className="hidden sm:inline">Yearly Planner</span>
          </TabsTrigger>
          <TabsTrigger value="resources" className="flex items-center gap-2 bg-amber-100">
            <FolderOpen className="h-4 w-4" />
            <span className="hidden sm:inline">Resources</span>
          </TabsTrigger>
          <TabsTrigger value="schemes" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            <span className="hidden sm:inline">Schemes</span>
          </TabsTrigger>
          <TabsTrigger value="lessons" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">Lessons</span>
          </TabsTrigger>
          <TabsTrigger value="term-exam" className="flex items-center gap-2 bg-rose-100">
            <FileQuestion className="h-4 w-4" />
            <span className="hidden sm:inline">Term Exam</span>
          </TabsTrigger>
          <TabsTrigger value="quick-print" className="flex items-center gap-2 bg-blue-100">
            <Files className="h-4 w-4" />
            <span className="hidden sm:inline">Quick Print</span>
          </TabsTrigger>
          <TabsTrigger value="ai-planner" className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            <span className="hidden sm:inline">AI Planner</span>
          </TabsTrigger>
        </TabsList>

        {/* Yearly Planner Tab */}
        <TabsContent value="yearly-planner" className="mt-6 space-y-6">
          {/* Term Selection */}
          <div className="flex flex-wrap gap-2">
            {['term1', 'term2', 'term3', 'term4'].map((term) => (
              <Button
                key={term}
                variant={selectedTerm === term ? "default" : "outline"}
                onClick={() => setSelectedTerm(term)}
                className={selectedTerm === term ? "bg-purple-600 hover:bg-purple-700" : ""}
              >
                Term {term.replace('term', '')}
              </Button>
            ))}
          </div>

          {/* Term Overview */}
          {eslYearlyPlanner?.[`year${selectedYear.replace('Year ', '')}`]?.[selectedTerm] && (
            <Card>
              <CardHeader className="bg-purple-50 border-b border-purple-100">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-purple-800">Term {selectedTerm.replace('term', '')} Overview</CardTitle>
                    <CardDescription className="text-purple-700">
                      {eslYearlyPlanner[`year${selectedYear.replace('Year ', '')}`][selectedTerm].weeks} • {eslYearlyPlanner[`year${selectedYear.replace('Year ', '')}`][selectedTerm].focus}
                    </CardDescription>
                  </div>
                  <Badge className="bg-amber-100 text-amber-700">
                    {eslYearlyPlanner[`year${selectedYear.replace('Year ', '')}`][selectedTerm].assessment}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="space-y-4">
                  {eslYearlyPlanner[`year${selectedYear.replace('Year ', '')}`][selectedTerm].units?.map((unit, idx) => (
                    <div key={idx} className="p-4 border rounded-lg hover:border-purple-300 transition-colors">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-purple-800">{unit.title}</h4>
                        <Badge variant="outline" className="text-purple-600">{unit.weeks}</Badge>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        <span className="font-medium">{unit.strand}</span> - Sub-strand {unit.subStrand}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {unit.topics?.map((topic, tIdx) => (
                          <Badge key={tIdx} variant="outline" className="text-xs bg-purple-50">
                            {topic}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Strand Hours Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Strand Hours Allocation</CardTitle>
              <CardDescription>Total instructional hours per strand for {selectedYear}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {curriculum.strands?.map((strand) => (
                  <div key={strand.id} className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="font-medium">{strand.name}</span>
                      <span className="text-gray-600">{strand.hours} hours ({strand.weight}%)</span>
                    </div>
                    <Progress value={strand.weight} className="h-2" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Resources Tab */}
        <TabsContent value="resources" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download className="w-5 h-5" />
                Teaching Resources - {selectedYear}
              </CardTitle>
              <CardDescription>Reference materials, templates, and guides for ESL teaching</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {(eslResources?.[`year${selectedYear.replace('Year ', '')}`] || []).map((resource, idx) => (
                  <div key={idx} className="p-4 border rounded-lg hover:border-purple-300 hover:shadow-sm transition-all">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium text-purple-800">{resource.title}</h4>
                      <Badge variant="outline" className="text-purple-600 text-xs">{resource.type}</Badge>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{resource.description}</p>
                    <div className="flex items-center justify-between">
                      <Badge className="bg-gray-100 text-gray-600">{resource.unit}</Badge>
                      <Button size="sm" variant="outline" className="text-purple-600 hover:text-purple-700">
                        <Download className="w-4 h-4 mr-1" />
                        Download
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Assessment Resources */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5" />
                Assessment Structure
              </CardTitle>
              <CardDescription>Assessment guidelines and weighting for {selectedYear}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                  <h4 className="font-medium text-purple-800 mb-2 flex items-center gap-2">
                    <GraduationCap className="w-4 h-4" />
                    External Examination ({curriculum.assessment?.externalExamination}%)
                  </h4>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p>End of year national examination covering all strands</p>
                  </div>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-100">
                  <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                    <ClipboardCheck className="w-4 h-4" />
                    Internal Assessment ({curriculum.assessment?.internalAssessment}%)
                  </h4>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p>CATs and TDTs throughout the year</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Term Exam Tab */}
        <TabsContent value="term-exam" className="mt-6">
          <TermExamGenerator
            subject="ESL"
            year={parseInt(selectedYear.replace('Year ', ''))}
            unitTests={[]}
            subjectColor="purple"
          />
        </TabsContent>

        {/* Quick Print Tab */}
        <TabsContent value="quick-print" className="mt-6">
          <QuickPrintAll
            subject="ESL"
            year={parseInt(selectedYear.replace('Year ', ''))}
            weeklyLessons={{}}
            subjectColor="purple"
          />
        </TabsContent>

        {/* Curriculum Content */}
        <TabsContent value="curriculum" className="mt-6">
          <div className="grid gap-6">
            {curriculum.strands?.map((strand, strandIndex) => {
              const IconComponent = getIconComponent(strand.icon);
              return (
                <Card key={strand.id || strandIndex} className="overflow-hidden">
                  <CardHeader className={`bg-gradient-to-r from-${strand.color || 'purple'}-100 to-${strand.color || 'purple'}-50`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 bg-${strand.color || 'purple'}-200 rounded-lg`}>
                          <IconComponent className={`h-6 w-6 text-${strand.color || 'purple'}-700`} />
                        </div>
                        <div>
                          <CardTitle className="text-lg">
                            Strand {strandIndex + 1}: {strand.name}
                          </CardTitle>
                          <CardDescription>{strand.outcome}</CardDescription>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant="secondary">{strand.hours} hours</Badge>
                        <div className="text-sm text-gray-500 mt-1">{strand.weight}% weight</div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-4">
                    <Accordion type="single" collapsible className="w-full">
                      {strand.subStrands?.map((subStrand, subIndex) => (
                        <AccordionItem key={subStrand.id || subIndex} value={`${strandIndex}-${subIndex}`}>
                          <AccordionTrigger className="hover:no-underline">
                            <div className="flex items-center gap-2">
                              <Target className="h-4 w-4 text-purple-500" />
                              <span className="font-medium">{subStrand.name}</span>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent>
                            <div className="pl-6 space-y-4">
                              <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                                <strong>Learning Outcome:</strong> {subStrand.outcome}
                              </div>
                              <div className="space-y-2">
                                <h4 className="font-medium text-gray-900 flex items-center gap-2">
                                  <ClipboardCheck className="h-4 w-4" />
                                  Learning Topics
                                </h4>
                                <div className="grid gap-2">
                                  {subStrand.topics?.map((topic, topicIndex) => (
                                    <div key={topicIndex} className="flex items-start gap-3 p-2 bg-white border rounded-lg">
                                      <CheckCircle2 className="h-4 w-4 text-green-500 mt-1 flex-shrink-0" />
                                      <div className="flex-1">
                                        <div className="text-sm">{topic.topic}</div>
                                        <div className="text-xs text-gray-500 mt-1">Code: {topic.code}</div>
                                      </div>
                                      {getSkillLevelBadge(topic.level)}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Assessment Information */}
          {curriculum.assessment && (
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GraduationCap className="h-5 w-5" />
                  Assessment Structure
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium mb-3">Assessment Weighting</h4>
                    <div className="space-y-3">
                      {curriculum.assessment.internalExamination && (
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Internal Examination</span>
                            <span>{curriculum.assessment.internalExamination}%</span>
                          </div>
                          <Progress value={curriculum.assessment.internalExamination} className="h-2" />
                        </div>
                      )}
                      {curriculum.assessment.externalExamination && (
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>External Examination</span>
                            <span>{curriculum.assessment.externalExamination}%</span>
                          </div>
                          <Progress value={curriculum.assessment.externalExamination} className="h-2" />
                        </div>
                      )}
                      {curriculum.assessment.continuousAssessment && (
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Continuous Assessment</span>
                            <span>{curriculum.assessment.continuousAssessment}%</span>
                          </div>
                          <Progress value={curriculum.assessment.continuousAssessment} className="h-2" />
                        </div>
                      )}
                      {curriculum.assessment.internalAssessment && (
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Internal Assessment</span>
                            <span>{curriculum.assessment.internalAssessment}%</span>
                          </div>
                          <Progress value={curriculum.assessment.internalAssessment} className="h-2" />
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium mb-3">Strand Weighting</h4>
                    <div className="space-y-2">
                      {curriculum.assessment.strandWeighting && Object.entries(curriculum.assessment.strandWeighting).map(([strand, weights]) => (
                        <div key={strand} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                          <span className="text-sm capitalize">{strand.replace(/([A-Z])/g, ' $1').trim()}</span>
                          <Badge variant="outline">{weights.total}%</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Schemes Tab */}
        <TabsContent value="schemes" className="mt-6">
          <SubjectSchemeOfWork
            subject="English as Second Language"
            year={parseInt(selectedYear.replace('Year ', ''))}
            weeklyLessons={eslWeeklyLessons}
            subjectColor="purple"
          />
        </TabsContent>

        {/* Lessons Tab */}
        <TabsContent value="lessons" className="mt-6">
          <LessonPlanGenerator
            subject="English as Second Language"
            curriculum={curriculum}
            weeklyLessons={eslWeeklyLessons}
            selectedYear={selectedYear}
          />
        </TabsContent>

        {/* AI Planner Tab */}
        <TabsContent value="ai-planner" className="mt-6">
          <AILessonPlanGenerator 
            subject="English as Second Language"
            curriculum={curriculum}
            selectedYear={selectedYear}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
